/**
 * MKS-358 — how the capacity planner writes an allocation.
 *
 * The ticket's ask is that a person-month reads as "how much of them is
 * booked, and how many hours that is" — `60 % · 84 h`. FTE is the unit the
 * planner used to lead with; behind `mks_358_planner_allocation_ui` it drops
 * to a tooltip, because 0.6 answers neither question a planner asks (a
 * half-timer at 0.5 FTE is fully booked, not half booked).
 *
 * Numbers are trimmed rather than padded, matching `formatFte`'s rule from
 * Phase 34 ("klidně se může zobrazovat 1 a ne 1,00"): 60 reads `60`, 62.5
 * reads `62.5`. The decimal separator stays `.` like every other capacity
 * surface, and no `Intl`/`toLocale*` call is involved, so this adds nothing
 * for `localeFormatGuard` to catch.
 */

/** Trim a number to at most `decimals` places, dropping trailing zeros. */
function trimmed(value: number, decimals: number): string {
  return parseFloat(value.toFixed(decimals)).toString();
}

/**
 * `60` / `62.5` — a share of a capacity, as the backend computed it.
 *
 * MKS-535: WHICH capacity is the backend's call, not this function's. Behind
 * `mks_535_percent_of_available` it is the hours the person is actually
 * available for that month (their FTE less approved time off and public
 * holidays), so 100 means fully booked and a figure ABOVE 100 is a real
 * reading, not a bug — `125 %` is someone booked into time off. With the flag
 * off it is still the month capacity before time off. Nothing is clamped here:
 * a cap would hide exactly the case the planner needs to see.
 */
export function formatAllocationPercent(percent: number): string {
  if (!Number.isFinite(percent)) return '—';
  return `${trimmed(percent, 1)} %`;
}

/** `84` / `83.5` — hours in the month. */
export function formatAllocationHours(hours: number): string {
  if (!Number.isFinite(hours)) return '—';
  return `${trimmed(hours, 1)} h`;
}

/**
 * The primary cell figure: `60 % · 84 h`. Both halves always render, so a
 * reader never has to guess which unit a bare number is in.
 */
export function formatAllocation(percent: number, hours: number): string {
  return `${formatAllocationPercent(percent)} · ${formatAllocationHours(hours)}`;
}
